import { defaultImage } from 'data/DefaultImages/DefaultArticleImage';
import {
  ArticleRichImage,
  UpNextArticle,
  SingleArticlePrimaryImage,
  SingleArticle,
  SingleArticleMetadataTopic,
  Metadata,
  SingleArticleRelatedArticleImage,
} from './types';
import { UpNextArticleQuery_upNextArticle_items_primaryImage } from 'graphql/__generated__/operations/UpNextArticleQuery';
import { getDescribedImage } from 'data/global/helpers';
import { fetchArticlesBodiesBySlugs } from 'data/global/data';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { ArticleCard } from 'data/global/types';
import cloneDeep from 'lodash.clonedeep';

export const relatedArticleImage = (
  image:
    | SingleArticleRelatedArticleImage
    | UpNextArticleQuery_upNextArticle_items_primaryImage
    | null
    | undefined,
): ArticleRichImage => ({
  alt: getDescribedImage(image),
  url: image?.image?.url ?? defaultImage(),
  title: image?.image?.title ?? '',
  maxWidth: image?.maxWidth ?? null,
});

export const articleImage = (
  image: SingleArticlePrimaryImage | null,
): ArticleRichImage => {
  if (!image) {
    return {
      url: '',
      alt: '',
      title: '',
      caption: {},
      maxWidth: null,
    };
  }

  return {
    alt: getDescribedImage(image),
    caption: image.caption,
    // if url === '' img elem won't be rendered
    url: image.image?.url ?? '',
    title: image.image?.title ?? '',
    maxWidth: image?.maxWidth ?? null,
  };
};

export const setShortDescriptionFallback = async (
  articleCards: ArticleCard[],
) => {
  const articlesToProceed = cloneDeep(articleCards);
  const articlesWithoutDescription = articleCards
    .filter((ra) => ra && ra.slug && !ra.description)
    .map((ra) => ra.slug) as string[];

  const articleBodies = await fetchArticlesBodiesBySlugs(
    articlesWithoutDescription,
  );
  const convertedBodies = articleBodies.map((ab) => ({
    text: documentToPlainTextString(ab?.body?.json),
    slug: ab?.slug,
  }));

  articlesToProceed.forEach((article) => {
    if (article && !article.description) {
      const firstSentence = convertedBodies.find(
        (cb) => cb.slug === article?.slug,
      )?.text;
      if (firstSentence) {
        article.description = firstSentence.slice(
          0,
          firstSentence.indexOf('.') + 1,
        );
      }
    }
  });

  return articlesToProceed;
};
/**
 * Get all related articles in an Article
 */
export const articleRelatedArticles = async (
  relatedArticles: (SingleArticle | null)[],
): Promise<UpNextArticle[]> => {
  if (!relatedArticles.length) {
    return [];
  }

  const articlesWithoutDescription = relatedArticles
    .filter((ra) => ra && ra.slug && !ra.shortDescription)
    .map((ra) => (ra as SingleArticle).slug) as string[];

  const articleBodies = await fetchArticlesBodiesBySlugs(
    articlesWithoutDescription,
  );
  const convertedBodies = articleBodies.map((ab) => ({
    text: documentToPlainTextString(ab?.body?.json),
    slug: ab?.slug,
  }));

  return relatedArticles.map((relatedArticle) => {
    let shortDescription = relatedArticle?.shortDescription || '';
    if (shortDescription) {
      const firstSentence = convertedBodies.find(
        (cb) => cb.slug === relatedArticle?.slug,
      )?.text;
      if (firstSentence) {
        shortDescription =
          firstSentence.slice(0, firstSentence.indexOf('.') + 1) || '';
      }
    }
    return {
      category:
        // At this point, we only ever need to display a single category on the related article, so we're grabbing the first one here.
        /**
         * @TODO We should return the child category. Not the first one.
         */
        relatedArticle?.categoriesCollection?.items[0]?.title ?? '',
      slug: relatedArticle?.slug ?? '',
      shortDescription,
      title: relatedArticle?.title ?? '',
      image: relatedArticleImage(relatedArticle?.primaryImage),
    };
  });
};

export const articleMetadata = (
  metadata: SingleArticleMetadataTopic | null | undefined,
): Metadata => {
  return {
    seoTitle: metadata?.seoTitle ?? '',
    seoDescription: metadata?.seoDescription ?? '',
    socialImage: metadata?.socialImage || null,
  };
};

export const instagramEmbedScriptUri = 'https://www.instagram.com/embed.js';
export const twitterEmbedScriptUri = 'https://platform.twitter.com/widgets.js';

const instagramEmbedScriptTag = `<script async src="${instagramEmbedScriptUri}"></script>`;
const twitterEmbedScriptTag = `<script async src="${twitterEmbedScriptUri}" charset="utf-8"></script>`;

export const checkIfInstagramEmbed = (dataStr: string) => {
  return dataStr?.indexOf(instagramEmbedScriptTag) !== -1;
};

export const checkIfTwitterEmbed = (dataStr: string) => {
  return dataStr?.indexOf(twitterEmbedScriptTag) !== -1;
};

export const removeInstagramEmbedTag = (dataStr: string) => {
  return dataStr.replace(instagramEmbedScriptTag, '');
};

export const removeTwitterEmbedTag = (dataStr: string) => {
  return dataStr.replace(instagramEmbedScriptTag, '');
};
